// frontend/src/farm/podworkoPlot.mjs
// Podwórko beside the house in the garden view (spec 2026-10-22-podworko-in-garden-design.md).
// Pure — no Phaser — so `node --test` covers it.
//
// Everything here is measured from the garden's home (left) corner, in cells: rx = x, ry = y − size
// (iso.mjs). The garden grows away from that corner, so none of this moves when it grows. The
// left→bottom fence is the line ry = 0; the house and Podwórko stand on one plot in front of it.
import { gardenGateX } from './iso.mjs';

const HW = 48, HH = 24;   // iso half tile (iso.TILE_W / 2, iso.TILE_H / 2)

// The plot kept clear for the house and Podwórko, and Podwórko on it (owner 2026-10-22): drawn twice as big
// as at first so the garage is taller than the player, lower right of the house along the fence line, its
// back with the gate by the path from the garden, its front far toward the street, with a gap in the hedge
// as the way in from the house; its left corner (rx0) stands 1.9 cells of lawn clear of the house's right
// wall (rx 3.4). rx1 and ry0 are nominal: scripts/farm_podworko.py sizes each level's picture so its gate is
// on PODWORKO_GATE.rx, which puts the right corner and the back within a few tenths of them.
export const PLOT = { rx0: -2.2, rx1: 21.5, ry0: 3.6, ry1: 17.4 };
export const PODWORKO = { rx0: 5.3, rx1: 20.5, ry0: 4.7, ry1: 17.2 };
// Podwórko's gate in its back fence, on the centre line of the cells x = 11 (= iso.GARDEN_GATE_X).
export const PODWORKO_GATE = { rx: 11.5, ry: PODWORKO.ry0 };
const PATH_TURN = 2;     // the home-relative row along which the path runs right, between the two gates
const PATH_LAST = 5;     // its last row: past the gate (ChatGPT puts it 0.4–0.7 deeper than ry0), under the picture
// The Podwórko picture's canvas, in home-relative DISPLAY px. The art is stored at 1× (ChatGPT's picture
// has no more detail at this size), and scripts/farm_podworko.py fits every level onto it with the gate
// and the plot's left corner on fixed points, so one rectangle places all five levels.
export const PODWORKO_CANVAS = { x0: -624, y0: 24, w: 1408, h: 896 };
// A garden car is drawn at FARM_CAR_SCALE of its art (scripts/farm_cars.py keeps the models' relative sizes;
// the widest, a Q5, is 400 px): small enough to stand in front of one garage door of the 2x Podwórko.
export const FARM_CAR_SCALE = 0.48;
export const FARM_CAR_W = 400 * FARM_CAR_SCALE;

export function homeRel(rx, ry) { return { x: (rx - ry) * HW, y: (rx + ry) * HH }; }
export function homeToLocal(rx, ry, size) {
  const p = homeRel(rx, ry);
  return { x: p.x, y: p.y + size * HH };
}

// The path tiles from the garden gate to Podwórko's (cells outside the farm, like iso.pathCells): straight
// down from a garden gate in line with Podwórko's; from a small garden's gate (iso.gardenGateX), out, right
// along the fence line to the gate's column, and down into it.
export function podworkoPathCells(size) {
  const gx = gardenGateX(size), x1 = PODWORKO_GATE.rx - 0.5;
  const turn = gx === x1 ? PATH_LAST : PATH_TURN;
  const cells = [];
  for (let ry = 0; ry <= turn; ry++) cells.push({ x: gx, y: size + ry });
  for (let x = gx + 1; x <= x1; x++) cells.push({ x, y: size + turn });
  for (let ry = turn + 1; ry <= PATH_LAST; ry++) cells.push({ x: x1, y: size + ry });
  return cells;
}

// Coming in from Podwórko: the cell inside the garden next to the gate cell.
export function podworkoArrivalCell(size) { return { x: gardenGateX(size), y: size - 2 }; }

// Podwórko as the garden draws it, from your own state ({ backyard, cars: { owned } }) or a visit
// view ({ backyard, cars: [garage cars] }); null while it is locked.
export function podworkoState(data) {
  const b = data?.backyard;
  if (!b || b.locked !== false) return null;
  const level = Math.min(5, Math.max(1, Math.floor(Number(b.level) || 1)));
  const cars = Array.isArray(data.cars)
    ? data.cars
    : (data.cars?.owned ?? []).filter((c) => c?.location?.scene === 'garage');
  return { level, cars: cars.map((c) => c.model) };
}

// What the garden must repaint for: the lock, the level and the garage cars.
export function podworkoSig(st) { return st ? `${st.level}|${st.cars.join(',')}` : 'locked'; }

// The picture's rectangle in the FarmWorld container's local px, and the y it sorts at (its front corner).
export function podworkoSpriteRect(size) {
  const c = PODWORKO_CANVAS;
  return { x0: c.x0, y0: c.y0 + size * HH, w: c.w, h: c.h, depthY: homeToLocal(PODWORKO.rx1, PODWORKO.ry1, size).y };
}

// A car picture's origin: the middle of its footprint, so it stands ON the pad its spot marks (owner
// 2026-10-22: the cars stood up-left of their pads, hung from their bottom edge). Drawn at the garden's 2:1,
// a car's footprint is half as tall as the picture is wide, with its lowest point at the bottom.
export function carOrigin(w, h) { return [0.5, Math.max(0, 1 - w / 4 / h)]; }

// Where the index-th garage car stands (the middle of its pad), or null when that level has no such spot.
// `spots` = farm/podworkoAnchors.mjs PODWORKO_CAR_SPOTS (canvas display px per level).
export function carSpot(level, index, size, spots) {
  const s = spots?.[level]?.[index];
  if (!s) return null;
  const r = podworkoSpriteRect(size);
  return { x: r.x0 + s.x, y: r.y0 + s.y };
}

// Lawn props stay off the plot, a strip of lawn in front of it (a tall tree there would hide it) and
// the path from the garden gate (the one this garden size has).
export function inPlotClear(rx, ry, size) {
  const plot = rx >= PLOT.rx0 - 1 && rx <= PLOT.rx1 + 1 && ry >= PLOT.ry0 - 0.5 && ry <= PLOT.ry1 + 3;
  const path = ry >= 0 && podworkoPathCells(size).some((c) => rx >= c.x - 0.5 && rx <= c.x + 1.5 && ry >= c.y - size - 0.5 && ry <= c.y - size + 1.5);
  return plot || path;
}
